import { computed, ref } from 'vue'
import ScreenViewToggle from '@/global/components/table/ScreenViewToggle.vue'

/**
 * How a listing splits the screen with the side panel beside it - the map, on every
 * screen that offers this today.
 */
export const SCREEN_VIEWS = {
  // The listing across the whole width, no panel.
  table: 'table',
  // Listing and panel side by side.
  split: 'split',
  // The panel across the whole width, no listing.
  map: 'map'
}

/**
 * What the listing itself takes of the screen, over the same three thirds in every
 * view: all of it, two thirds with the panel in the rest, or none of it. One
 * denominator throughout is what makes the three comparable - "1/1" and "2/3" beside
 * each other named two different wholes, and neither of them said what the third view
 * would be.
 */
export const SCREEN_VIEW_LABELS = {
  [SCREEN_VIEWS.table]: '3/3',
  [SCREEN_VIEWS.split]: '2/3',
  [SCREEN_VIEWS.map]: '0/3'
}

/**
 * The class names the split is set up with. Applied through DataTable's `globalStyle`;
 * the rules themselves are global (see `global/assets/css/style.css`), since every
 * screen splits the same way.
 */
export const SCREEN_VIEW_CLASSES = {
  splitRow: 'screen-split-row',
  splitPanelCol: 'screen-panel-col',
  fullPanelCol: 'screen-panel-col--full'
}

/**
 * One screen's choice of view.
 *
 * Kept at module level by the screen that creates it, because the toggle button - which
 * DataTable renders inside its own toolbar, through `filters_row_end_components` - and
 * the table config that acts on the choice live in separate component trees. DataTable
 * hands those toolbar components a fixed set of props, so there is no prop/event pair
 * to thread through it; `toggle` below is the shared button already bound to this
 * state, ready to be registered as it is.
 *
 * @param {Object} [options]
 * @param {string} [options.initialView] Which view the screen opens with.
 * @param {string[]} [options.views] Which views it offers, in the order they are listed.
 */
export function createScreenView (options = {}) {
  const {
    initialView = SCREEN_VIEWS.split,
    views = Object.values(SCREEN_VIEWS)
  } = options

  const offeredViews = views.filter(view => !!SCREEN_VIEWS[view])
  // What the button lists: the button itself knows nothing about the views.
  const offeredOptions = offeredViews.map(view => ({
    value: view,
    label: SCREEN_VIEW_LABELS[view]
  }))
  const screenView = ref(offeredViews.includes(initialView) ? initialView : offeredViews[0])

  const setScreenView = (view) => {
    if (!offeredViews.includes(view)) {
      return
    }

    screenView.value = view
  }

  const isPanelVisible = computed(() => screenView.value !== SCREEN_VIEWS.table)
  const isTableVisible = computed(() => screenView.value !== SCREEN_VIEWS.map)
  // Both on the row at once, which is the only view where either of them is held to a
  // share of it.
  const isSplitView = computed(() => screenView.value === SCREEN_VIEWS.split)

  const globalStyle = computed(() => ({
    // The table's own column has no class hook of its own, so the split is set up from
    // the row. Only while the two share it - on its own, either one takes the full
    // width.
    tableRowClasses: isSplitView.value ? SCREEN_VIEW_CLASSES.splitRow : '',
    sideTableContentColClasses: isSplitView.value
      ? SCREEN_VIEW_CLASSES.splitPanelCol
      : SCREEN_VIEW_CLASSES.fullPanelCol
  }))

  // The shared button, bound to this screen's state. A render function rather than a
  // component file per screen: DataTable passes toolbar components a fixed set of
  // props, so the binding cannot come from the config that registers it.
  const toggle = {
    name: 'ScreenViewToggleBinding',
    render (createElement) {
      return createElement(ScreenViewToggle, {
        props: {
          view: screenView.value,
          options: offeredOptions
        },
        on: {
          select: setScreenView
        }
      })
    }
  }

  return {
    screenView,
    views: offeredViews,
    setScreenView,
    isPanelVisible,
    isTableVisible,
    isSplitView,
    globalStyle,
    toggle
  }
}
